import {gql} from '@apollo/client'
import * as t from 'io-ts'
import React, {useState} from 'react'
import {Helmet} from 'react-helmet-async'
import {Link} from 'react-router-dom'

import {ListLayout} from '.'
import {Error} from '../../../components/auth/layout'
import {ButtonGroup} from '../../../components/form'
import {Button, LinkButton} from '../../../components/library/buttons'
import {
  DropdownBody,
  DropdownFooter,
  DropdownItem
} from '../../../components/library/dropdown/content'
import {TextInput} from '../../../components/library/form'
import {List, ListColumns} from '../../../components/library/list'
import {Modal} from '../../../components/library/modal/modal'
import {OverflowFooter} from '../../../components/overflow'
import {
  CustomRolesOrderBy,
  PermissionAction,
  PermissionResource
} from '../../../data/workbench-api/generated/types'
import {usePersistedState} from '../../../hooks/use-persisted-state'
import {notify} from '../../../lib/bugsnag'
import {OrganisationNode} from '../../../organisation'
import {
  RoleRowFragment,
  useRemoveCustomRoleMutation,
  useRolesQuery
} from './generated/roles'

const columns: ListColumns<RoleRowFragment, CustomRolesOrderBy> = [
  {
    id: 'role',
    header: 'Role',
    accessor: r => (
      <Link to={`../role/${r.id}`}>
        <strong>{r.name}</strong>
      </Link>
    ),
    sortKey: CustomRolesOrderBy.NameAsc
  },
  {
    id: 'description',
    header: 'Description',
    accessor: r => r.description
  },
  {
    id: 'ID',
    header: 'id',
    accessor: r => r.id
  }
]

export const Roles = ({organisation}: {organisation: OrganisationNode}) => {
  const [orderBy, setOrderBy] = usePersistedState<CustomRolesOrderBy>(
    CustomRolesOrderBy.NameAsc,
    'custom-role-order-by',
    // @ts-ignore we don't have a codec for this
    t.string,
    true
  )
  const {data, loading} = useRolesQuery({
    variables: {organisationId: organisation.id, orderBy: [orderBy]}
  })
  const [roleToDelete, setRoleToDelete] = useState<RoleRowFragment | null>(null)
  // TODO: may be to expand analytics.track to include the delete role

  const canEdit = organisation.hasPermission(
    PermissionResource.CustomRole,
    PermissionAction.Edit
  )

  return (
    <>
      <Helmet>
        <title>Workbench roles</title>
      </Helmet>
      <ListLayout
        header={
          <>
            <p>
              A role bundles together access to resources in the Workbench, and
              levels of permissions that are assigned to these resources.
            </p>

            {canEdit ? (
              <LinkButton disabled={!canEdit} to="../role">
                Create a new role
              </LinkButton>
            ) : (
              // hack until link buttons support disabled styles
              <Button disabled={true}>Create a new role</Button>
            )}
          </>
        }
      >
        <List<RoleRowFragment, CustomRolesOrderBy>
          bleed={'#f2f2f2'}
          columns={columns}
          loading={loading && !data}
          rows={data?.customRoles?.nodes ?? []}
          renderOverflow={(role, closeDropdown) => (
            <Overflow
              role={role}
              canEdit={canEdit}
              onDelete={() => {
                closeDropdown()
                setRoleToDelete(role)
              }}
            />
          )}
          setOrderBy={setOrderBy}
          orderBy={[orderBy]}
        />
      </ListLayout>

      {roleToDelete ? (
        <Modal
          title="Delete role"
          handleClose={() => setRoleToDelete(null)}
          stopPropagation={true}
          style={{maxWidth: '500px'}}
          order="up"
        >
          <DeleteRole
            role={roleToDelete}
            onCancel={() => setRoleToDelete(null)}
            onDelete={() => {
              setRoleToDelete(null)
            }}
          />
        </Modal>
      ) : null}
    </>
  )
}

const Overflow = ({
  role,
  canEdit,
  onDelete
}: {
  role: RoleRowFragment
  canEdit: boolean
  onDelete: () => void
}) => {
  return (
    <>
      <DropdownBody>
        <DropdownItem disabled={!canEdit}>
          <Link to={`../role/${role.id}`}>Edit</Link>
        </DropdownItem>
        <DropdownItem disabled={!canEdit}>
          <a onClick={onDelete}>Delete</a>
        </DropdownItem>
      </DropdownBody>
      <DropdownFooter>
        <OverflowFooter id={role.id}></OverflowFooter>
      </DropdownFooter>
    </>
  )
}

const DeleteRole = ({
  role,
  onCancel,
  onDelete
}: {
  role: RoleRowFragment
  onCancel: () => void
  onDelete: () => void
}) => {
  const [roleDeleteError, setRoleDeleteError] = useState<string | undefined>(
    undefined
  )
  const [deleteCustomRoleMutation, {loading: deleting}] =
    useRemoveCustomRoleMutation({
      refetchQueries: ['Roles'],
      onError: error => {
        setRoleDeleteError(error.message)
        notify(`Error deleting a role `, {
          id: role.id,
          name: role.name,
          errorMessage: error.message
        })
      },
      onCompleted: () => {
        onDelete()
      }
    })
  const [confirmationPrompt, setConfirmationPrompt] = useState<string>('')
  const groupList = role.grants.nodes.map(
    grant => grant.organisationGroup?.name ?? grant.groupId
  )
  const confirmationValue = groupList.at(0)

  return (
    <>
      {groupList.length ? (
        <>
          {roleDeleteError ? <Error>{roleDeleteError}</Error> : null}
          <span>
            Role <b>{role.name}</b> is present in the{' '}
            {groupList.length === 1 ? (
              <>
                <b>{groupList.at(0)}</b> group.
              </>
            ) : (
              <>
                following groups:
                <ul>
                  {groupList.map(group => (
                    <li key={group}>{group}</li>
                  ))}
                </ul>
              </>
            )}
          </span>
          <p>
            Please enter the name of the {groupList.length > 1 ? 'first' : ''}{' '}
            group to confirm deletion.
          </p>
          <TextInput
            label="Confirm deletion"
            value={confirmationPrompt}
            onChange={v => {
              setConfirmationPrompt(v)
            }}
          />
        </>
      ) : (
        <span>
          Are you sure you want to delete the role <b>{role.name}</b>?
        </span>
      )}

      <ButtonGroup>
        <Button size="large" onClick={onCancel} disabled={deleting}>
          Cancel
        </Button>
        <Button
          appearance="warning"
          size="large"
          onClick={async () => {
            await deleteCustomRoleMutation({
              variables: {
                id: role.id
              }
            })
          }}
          disabled={
            (groupList.length && confirmationPrompt !== confirmationValue) ||
            deleting
          }
        >
          Delete
        </Button>
      </ButtonGroup>
    </>
  )
}

Roles.fragments = {
  row: gql`
    fragment RoleRow on CustomRole {
      id
      name
      description
      grants: grantsByOrganisationIdAndRoleId {
        nodes {
          id
          groupId
          organisationGroup {
            id
            name
          }
        }
      }
    }
  `
}

gql`
  query Roles(
    $organisationId: String!
    $orderBy: [CustomRolesOrderBy!]! = [NAME_ASC]
  ) {
    customRoles(
      condition: {organisationId: $organisationId}
      orderBy: $orderBy
    ) {
      nodes {
        id
        ...RoleRow
      }
    }
  }
  ${Roles.fragments.row}
`

gql`
  mutation RemoveCustomRole($id: String!) {
    removeCustomRoleMutation(id: $id) {
      success
    }
  }
`
